import React, { forwardRef, ReactNode, useId } from 'react'
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { FaPaperclip, FaSearch, FaTimes } from 'react-icons/fa'
import Select, { GroupBase, Props as ReactSelectProps } from 'react-select'
import { cx } from '../cx'
import { reactSelectStyles, reactSelectTheme } from '../styles/reactSelectStyles'
import s from './styles.module.scss'
import 'react-datepicker/dist/react-datepicker.css'

// ---------------------------------------------------------------------- Field
export interface FieldProps {
    label?: ReactNode
    /** Connects label to the control; generated when omitted. */
    htmlFor?: string
    required?: boolean
    /** Shows "(optional)" after the label. */
    optional?: boolean
    hint?: ReactNode
    error?: ReactNode
    /** Label beside the control instead of above (checkboxes). */
    inline?: boolean
    /** Grid span helpers when inside FieldGrid. */
    span?: 1 | 2 | 'all'
    className?: string
    children: ReactNode | ((id: string) => ReactNode)
}

/** Label + control + hint/error. Wrap every form control in one. */
export const Field = ({ label, htmlFor, required, optional, hint, error, inline, span, className, children }: FieldProps) => {
    const generated = useId()
    const id = htmlFor ?? generated
    return (
        <div className={cx(s.field, inline && s.inline, span === 2 && s.span2, span === 'all' && s.spanAll, className)}>
            {label && (
                <label className={s.label} htmlFor={id}>
                    {label}
                    {required && (
                        <span className={s.required} aria-hidden="true">
                            *
                        </span>
                    )}
                    {optional && <span className={s.optional}>(optional)</span>}
                </label>
            )}
            {typeof children === 'function' ? children(id) : children}
            {error ? <p className={s.error}>{error}</p> : hint ? <p className={s.hint}>{hint}</p> : null}
        </div>
    )
}

export interface FieldGridProps extends React.HTMLAttributes<HTMLDivElement> {
    minColumn?: number
    children: ReactNode
}

/** Responsive form layout: fields auto-fill columns of `minColumn` width. */
export const FieldGrid = ({ minColumn = 220, className, style, children, ...rest }: FieldGridProps) => (
    <div className={cx(s.grid, className)} style={{ ...style, ['--min-col' as any]: `${minColumn}px` }} {...rest}>
        {children}
    </div>
)

// ---------------------------------------------------------------------- Input
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    invalid?: boolean
    size?: any
    /** Compact height. */
    small?: boolean
    mono?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({ invalid, small, mono, className, size, ...rest }, ref) => (
    <input ref={ref} className={cx(s.input, invalid && s.invalid, small && s.sm, mono && s.mono, className)} aria-invalid={invalid || undefined} {...rest} />
))
Input.displayName = 'Input'

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
    invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({ invalid, className, ...rest }, ref) => (
    <textarea ref={ref} className={cx(s.textarea, invalid && s.invalid, className)} aria-invalid={invalid || undefined} {...rest} />
))
Textarea.displayName = 'Textarea'

export interface NativeSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
    invalid?: boolean
    small?: boolean
    /** Convenience: renders `<option>`s from the list. */
    options?: Array<{ value: string | number; label: ReactNode; disabled?: boolean }>
    placeholder?: string
}

/** Native `<select>` styled like Input. Use `ReactSelect` for search/multi. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(({ invalid, small, options, placeholder, className, children, ...rest }, ref) => (
    <select ref={ref} className={cx(s.select, invalid && s.invalid, small && s.sm, className)} aria-invalid={invalid || undefined} {...rest}>
        {placeholder != null && (
            <option value="" disabled={rest.required}>
                {placeholder}
            </option>
        )}
        {options?.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
            </option>
        ))}
        {children}
    </select>
))
NativeSelect.displayName = 'NativeSelect'

export interface SearchInputProps extends Omit<InputProps, 'type' | 'onChange' | 'value'> {
    value: string
    onChange: (value: string) => void
    /** Show a clear button when there is a value (default true). */
    clearable?: boolean
}

/** Search box with leading icon and clear button. */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(({ value, onChange, clearable = true, className, placeholder = 'Search…', ...rest }, ref) => (
    <div className={cx(s.withIcon, className)}>
        <span className={s.icon}>
            <FaSearch aria-hidden="true" />
        </span>
        <Input ref={ref} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cx(clearable && value && s.hasClear)} {...rest} />
        {clearable && value && (
            <button type="button" className={s.clear} onClick={() => onChange('')} aria-label="Clear search">
                <FaTimes />
            </button>
        )}
    </div>
))
SearchInput.displayName = 'SearchInput'

/** Input with a prefix/suffix addon (e.g. "£" or "days"). */
export const InputGroup = ({ prefix, suffix, children, className }: { prefix?: ReactNode; suffix?: ReactNode; children: ReactNode; className?: string }) => (
    <div className={cx(s.inputGroup, className)}>
        {prefix && <span className={s.addon}>{prefix}</span>}
        {children}
        {suffix && <span className={s.addon}>{suffix}</span>}
    </div>
)

// ------------------------------------------------------------ Checkbox / Switch
export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label: ReactNode
    hint?: ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ label, hint, className, disabled, ...rest }, ref) => (
    <label className={cx(s.check, disabled && s.disabled, className)}>
        <input ref={ref} type="checkbox" disabled={disabled} {...rest} />
        <span className={s.checkText}>
            <span>{label}</span>
            {hint && <span className={s.checkHint}>{hint}</span>}
        </span>
    </label>
))
Checkbox.displayName = 'Checkbox'

export const Radio = forwardRef<HTMLInputElement, CheckboxProps>(({ label, hint, className, disabled, ...rest }, ref) => (
    <label className={cx(s.check, disabled && s.disabled, className)}>
        <input ref={ref} type="radio" disabled={disabled} {...rest} />
        <span className={s.checkText}>
            <span>{label}</span>
            {hint && <span className={s.checkHint}>{hint}</span>}
        </span>
    </label>
))
Radio.displayName = 'Radio'

export interface SwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
    label?: ReactNode
}

/** On/off toggle (Active flags). */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(({ label, className, disabled, ...rest }, ref) => (
    <label className={cx(s.switch, disabled && s.disabled, className)}>
        <input ref={ref} type="checkbox" role="switch" disabled={disabled} {...rest} />
        <span className={s.track} aria-hidden="true" />
        {label && <span>{label}</span>}
    </label>
))
Switch.displayName = 'Switch'

// ------------------------------------------------------------------ FileInput
export interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
    /** Text shown when nothing is selected. */
    placeholder?: string
    /** Current selection to display (controlled). */
    fileName?: string | null
    onChange: (files: FileList | null) => void
    block?: boolean
}

/** Dashed drop-style file picker. */
export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(({ placeholder = 'Choose file…', fileName, onChange, block, className, ...rest }, ref) => (
    <label className={cx(s.file, fileName && s.hasFile, block && s.block, className)}>
        <FaPaperclip aria-hidden="true" />
        <span>{fileName || placeholder}</span>
        <input ref={ref} type="file" onChange={(e) => onChange(e.target.files)} {...rest} />
    </label>
))
FileInput.displayName = 'FileInput'

// ---------------------------------------------------------------- ReactSelect
export type ReactSelectComponentProps<Option = unknown, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>> = ReactSelectProps<Option, IsMulti, Group> & {
    invalid?: boolean
}

/** `react-select` with the design tokens applied. Same API as react-select. */
export function ReactSelect<Option = unknown, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>({
    invalid,
    styles,
    ...rest
}: ReactSelectComponentProps<Option, IsMulti, Group>) {
    const merged = invalid
        ? {
              ...reactSelectStyles,
              ...styles,
              control: (base: any, state: any) => ({
                  ...(reactSelectStyles.control as any)(base, state),
                  borderColor: state.isFocused ? '#a85454' : '#cf9d9d',
                  boxShadow: state.isFocused ? '0 0 0 3px rgba(168, 84, 84, 0.16)' : 'none',
              }),
          }
        : { ...reactSelectStyles, ...styles }
    return <Select<Option, IsMulti, Group> styles={merged as any} theme={reactSelectTheme} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} menuPosition="fixed" {...rest} />
}

// ----------------------------------------------------------------- DatePicker
export interface DatePickerProps extends Omit<ReactDatePickerProps, 'className'> {
    invalid?: boolean
    className?: string
}

/** `react-datepicker` (dd/MM/yyyy, en-GB) with the design tokens applied. */
export const DatePicker = ({ invalid, className, dateFormat = 'dd/MM/yyyy', placeholderText = 'Select date', ...rest }: DatePickerProps) => (
    <div className={cx(s.datepickerHost, invalid && s.invalidHost, className)}>
        <ReactDatePicker dateFormat={dateFormat} placeholderText={placeholderText} showMonthDropdown showYearDropdown dropdownMode="select" popperPlacement="bottom-start" {...rest} />
    </div>
)
